import { useEffect } from 'react'
import { useUser } from '@paintscout/ui-business'

export interface DocumentTitleProps {
  title: string
}

/**
 * Sets the document.title
 *
 * While masquerading, the masqueraded company leads the title instead of the "PaintScout" prefix —
 * `Acme Painting · Dashboard` rather than `PaintScout | Dashboard`. Masquerade context is per-tab, so
 * staff can be in several customer accounts at once, and browser tabs truncate to a handful of
 * characters: a shared prefix makes every tab look identical exactly when telling them apart matters
 * most. The company name is the highest-value thing in that space, so it goes first and the prefix is
 * dropped rather than making the label longer.
 *
 * `useUser` defaults to an empty context outside a provider, so this stays safe on unauthenticated
 * pages that render a title.
 */
function DocumentTitle({ title }: DocumentTitleProps) {
  const { isMasquerading, user } = useUser()
  const companyName = user?.app_metadata?.company?.name

  const prefixedTitle = isMasquerading && companyName ? `${companyName} · ${title}` : `PaintScout | ${title}`

  useEffect(() => {
    document.title = prefixedTitle
  }, [prefixedTitle])

  return null
}

export default DocumentTitle
